<template>
  <b-card no-body class="dash-panel hw-card">
    <b-card-header class="dash-panel__header hw-card__header border-0">
      <div class="hw-card__top">
        <div class="dash-panel__heading">
          <span class="dash-panel__icon hw-card__icon">
            <feather-icon icon="BookOpenIcon" size="18" />
          </span>
          <div>
            <b-card-title class="dash-panel__title mb-0">{{
              $t("Homework Summary")
            }}</b-card-title>
            <small class="dash-panel__subtitle">{{
              $t("Subject-wise status for this campus")
            }}</small>
          </div>
        </div>

        <div class="hw-card__actions">
          <label class="hw-card__date">
            <feather-icon icon="CalendarIcon" size="15" />
            <flat-pickr
              v-model="hwDate"
              :config="config"
              class="form-control flat-picker hw-card__date-input"
              :placeholder="$t('Select Date')"
              @on-change="LoadData"
            />
          </label>
          <b-button
            variant="primary"
            class="hw-card__refresh"
            :disabled="loading"
            @click="LoadData"
          >
            <b-spinner v-if="loading" small class="mr-50" />
            <feather-icon v-else icon="RefreshCwIcon" size="15" class="mr-50" />
            {{ $t("Refresh") }}
          </b-button>
        </div>
      </div>
    </b-card-header>

    <!-- Class completion -->
    <div class="hw-progress-wrap">
      <div class="hw-progress__head">
        <span class="hw-progress__title">{{
          $t("Classes assigned homework")
        }}</span>
        <span class="hw-progress__value">
          <strong>{{ summary.markedCampuses || 0 }}</strong>
          / {{ summary.totalCampuses || 0 }}
        </span>
      </div>
      <div
        class="hw-progress"
        :title="`${markedPercent}% ${$t('assigned')}`"
      >
        <div class="hw-progress__fill" :style="{ width: `${markedPercent}%` }" />
      </div>
    </div>

    <!-- Stat tiles -->
    <div class="hw-stats">
      <div class="hw-stat hw-stat--total">
        <div class="hw-stat__top">
          <span class="hw-stat__icon">
            <feather-icon icon="LayersIcon" size="13" />
          </span>
          <span class="hw-stat__label">{{ $t("Total") }}</span>
        </div>
        <span class="hw-stat__value">{{
          (summary.totalHomework || 0).toLocaleString()
        }}</span>
        <div class="hw-stat__foot">
          <span class="hw-stat__pct">100%</span>
          <span class="hw-stat__hint">{{ $t("all subjects") }}</span>
        </div>
      </div>
      <div class="hw-stat hw-stat--ok">
        <div class="hw-stat__top">
          <span class="hw-stat__icon">
            <feather-icon icon="CheckCircleIcon" size="13" />
          </span>
          <span class="hw-stat__label">{{ $t("Assigned") }}</span>
        </div>
        <span class="hw-stat__value">{{
          (summary.totalAssigned || 0).toLocaleString()
        }}</span>
        <div class="hw-stat__foot">
          <span class="hw-stat__meter">
            <span
              class="hw-stat__meter-fill hw-stat__meter-fill--ok"
              :style="{ width: statShare(summary.totalAssigned) + '%' }"
            />
          </span>
          <span class="hw-stat__pct">{{ statShare(summary.totalAssigned) }}%</span>
        </div>
      </div>
      <div class="hw-stat hw-stat--published">
        <div class="hw-stat__top">
          <span class="hw-stat__icon">
            <feather-icon icon="SendIcon" size="13" />
          </span>
          <span class="hw-stat__label">{{ $t("Published") }}</span>
        </div>
        <span class="hw-stat__value">{{
          (summary.totalPublished || 0).toLocaleString()
        }}</span>
        <div class="hw-stat__foot">
          <span class="hw-stat__meter">
            <span
              class="hw-stat__meter-fill hw-stat__meter-fill--pub"
              :style="{ width: statShare(summary.totalPublished) + '%' }"
            />
          </span>
          <span class="hw-stat__pct">{{ statShare(summary.totalPublished) }}%</span>
        </div>
      </div>
      <div class="hw-stat hw-stat--pending">
        <div class="hw-stat__top">
          <span class="hw-stat__icon">
            <feather-icon icon="AlertCircleIcon" size="13" />
          </span>
          <span class="hw-stat__label">{{ $t("Unassigned") }}</span>
        </div>
        <span class="hw-stat__value">{{
          (summary.totalUnAssigned || 0).toLocaleString()
        }}</span>
        <div class="hw-stat__foot">
          <span class="hw-stat__meter">
            <span
              class="hw-stat__meter-fill hw-stat__meter-fill--pending"
              :style="{ width: statShare(summary.totalUnAssigned) + '%' }"
            />
          </span>
          <span class="hw-stat__pct"
            >{{ statShare(summary.totalUnAssigned) }}%</span
          >
        </div>
      </div>
      <div class="hw-stat hw-stat--muted">
        <div class="hw-stat__top">
          <span class="hw-stat__icon">
            <feather-icon icon="ClockIcon" size="13" />
          </span>
          <span class="hw-stat__label">{{ $t("Unpublished") }}</span>
        </div>
        <span class="hw-stat__value">{{
          (summary.totalUnPublished || 0).toLocaleString()
        }}</span>
        <div class="hw-stat__foot">
          <span class="hw-stat__meter">
            <span
              class="hw-stat__meter-fill hw-stat__meter-fill--muted"
              :style="{ width: statShare(summary.totalUnPublished) + '%' }"
            />
          </span>
          <span class="hw-stat__pct"
            >{{ statShare(summary.totalUnPublished) }}%</span
          >
        </div>
      </div>
    </div>

    <!-- Search + filters -->
    <div class="hw-toolbar">
      <div class="hw-search">
        <span class="hw-search__icon">
          <feather-icon icon="SearchIcon" size="15" />
        </span>
        <input
          v-model.trim="searchQuery"
          type="text"
          class="hw-search__input"
          :placeholder="$t('Search by class section')"
        />
        <span v-if="hasActiveFilter" class="hw-search__count">
          {{ filteredItems.length }} / {{ items.length }}
        </span>
      </div>

      <div class="hw-filters">
        <span class="hw-filters__label">{{ $t("Assigned") }}</span>
        <div class="hw-filters__group">
          <button
            v-for="opt in assignedFilterOptions"
            :key="`af-${opt.value}`"
            type="button"
            class="hw-filter"
            :class="[
              `hw-filter--${opt.tone}`,
              { 'is-active': filterAssigned === opt.value },
            ]"
            @click="setAssignedFilter(opt.value)"
          >
            {{ $t(opt.label) }}
          </button>
        </div>

        <span class="hw-filters__label">{{ $t("Published") }}</span>
        <div class="hw-filters__group">
          <button
            v-for="opt in publishedFilterOptions"
            :key="`pf-${opt.value}`"
            type="button"
            class="hw-filter"
            :class="[
              `hw-filter--${opt.tone}`,
              { 'is-active': filterPublished === opt.value },
            ]"
            @click="setPublishedFilter(opt.value)"
          >
            {{ $t(opt.label) }}
          </button>
        </div>

        <button
          v-if="hasActiveFilter"
          type="button"
          class="hw-filters__clear"
          @click="clearFilters"
        >
          <feather-icon icon="XIcon" size="12" class="mr-25" />
          {{ $t("Clear") }}
        </button>
      </div>
    </div>

    <div v-if="hasActiveFilter" class="hw-filter-summary">
      <feather-icon icon="FilterIcon" size="13" class="mr-25" />
      {{ filteredSubjectCount }} {{ $t("matching subjects") }}
      <span class="hw-filter-summary__sep">·</span>
      {{ filteredItems.length }} {{ $t("classes") }}
    </div>

    <div class="hw-table-wrap">
      <b-table
        class="hw-table mb-0"
        sticky-header="480px"
        show-empty
        :items="filteredItems"
        :fields="fields"
        hover
        :busy="loading"
      >
        <template #table-busy>
          <div class="hw-skeleton">
            <div v-for="r in 3" :key="`hw-sk-${r}`" class="hw-skeleton__group">
              <span class="hw-skeleton__class" />
              <div class="hw-skeleton__cards">
                <span
                  v-for="c in 4"
                  :key="`hw-sk-${r}-${c}`"
                  class="hw-skeleton__card"
                />
              </div>
            </div>
          </div>
        </template>

        <template #cell(class)="data">
          <div class="hw-class">
            <span class="hw-class__name">{{ data.item.classSection }}</span>
            <span class="hw-class__meta">
              {{ classSubjectCount(data.item) }} {{ $t("subjects") }}
            </span>
          </div>
        </template>

        <template #cell(details)="data">
          <div
            v-if="!subjectsForClass(data.item).length"
            class="hw-subjects-empty"
          >
            {{ $t("No subjects match the current filter") }}
          </div>
          <div v-else class="hw-subjects">
            <div
              v-for="(detail, index) in subjectsForClass(data.item)"
              :key="`${data.item.classSection}-${detail.subject}-${index}`"
              class="hw-subject"
              :class="{ 'is-done': isDone(detail.homeworkStatus) }"
            >
              <div class="hw-subject__head">
                <span class="hw-subject__name">{{ detail.subject }}</span>
                <span
                  v-if="detail.teacher"
                  class="hw-subject__teacher"
                  :title="detail.teacher"
                  >{{ detail.teacher }}</span
                >
              </div>
              <div class="hw-subject__status">
                <span
                  class="hw-chip"
                  :class="
                    isDone(detail.homeworkStatus) ? 'hw-chip--ok' : 'hw-chip--bad'
                  "
                >
                  <feather-icon
                    :icon="isDone(detail.homeworkStatus) ? 'CheckIcon' : 'XIcon'"
                    size="11"
                  />
                  <span class="hw-chip__text">{{
                    statusLabel(detail.homeworkStatus, "Not Assigned", "Assigned")
                  }}</span>
                </span>
                <span
                  class="hw-chip"
                  :class="
                    isDone(detail.publishedStatus)
                      ? 'hw-chip--pub'
                      : 'hw-chip--bad'
                  "
                >
                  <feather-icon
                    :icon="isDone(detail.publishedStatus) ? 'CheckIcon' : 'XIcon'"
                    size="11"
                  />
                  <span class="hw-chip__text">{{
                    statusLabel(detail.publishedStatus, "Not Published", "Published")
                  }}</span>
                </span>
              </div>
            </div>
          </div>
        </template>

        <template #empty>
          <div class="hw-empty">
            <feather-icon icon="InboxIcon" size="30" class="mb-50" />
            <div>{{ $t("No records found") }}</div>
          </div>
        </template>
      </b-table>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BTable,
  BSpinner,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";

export default {
  name: "HomeworkCard",
  components: {
    BCard,
    BCardHeader,
    BTable,
    BSpinner,
    flatPickr,
  },
  props: {
    campusID: {
      type: Number,
      required: true,
    },
    initialDate: {
      type: String,
      default: () => new Date().toJSON().split("T")[0],
    },
  },
  data() {
    return {
      searchQuery: "",
      filterAssigned: "all",
      filterPublished: "all",
      hwDate: this.initialDate,
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      loading: false,
      items: [],
      summary: {
        totalHomework: 0,
        totalAssigned: 0,
        totalPublished: 0,
        totalUnAssigned: 0,
        totalUnPublished: 0,
        totalCampuses: 0,
        markedCampuses: 0,
      },
      fields: [
        { key: "class", label: "Class & Section", tdClass: "align-top" },
        {
          key: "details",
          label: "Subjects & Homework Summary",
          tdClass: "align-top",
        },
      ],
    };
  },
  computed: {
    /** Classes that still have at least one subject after filtering. */
    filteredItems() {
      let list = this.items;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        list = list.filter((item) =>
          String(item.classSection || "")
            .toLowerCase()
            .includes(query)
        );
      }
      if (!this.hasActiveFilter) return list;
      // Keep only classes that still show at least one subject card.
      return list.filter((item) => this.subjectsForClass(item).length > 0);
    },
    hasActiveFilter() {
      return this.filterAssigned !== "all" || this.filterPublished !== "all";
    },
    /** Total subject cards visible after filtering. */
    filteredSubjectCount() {
      return this.filteredItems.reduce(
        (sum, item) => sum + this.subjectsForClass(item).length,
        0
      );
    },
    assignedFilterOptions() {
      return [
        { value: "all", label: "All", tone: "neutral" },
        { value: "yes", label: "Yes", tone: "ok" },
        { value: "no", label: "No", tone: "bad" },
      ];
    },
    publishedFilterOptions() {
      return [
        { value: "all", label: "All", tone: "neutral" },
        { value: "yes", label: "Yes", tone: "ok" },
        { value: "no", label: "No", tone: "bad" },
      ];
    },
    /** Share of classes that assigned at least one homework. */
    markedPercent() {
      const total = Number(this.summary.totalCampuses) || 0;
      if (total <= 0) return 0;
      const marked = Number(this.summary.markedCampuses) || 0;
      return Math.min(100, Math.round((marked / total) * 100));
    },
  },
  created() {
    this.LoadData();
  },
  methods: {
    ...mapActions({ get: "get" }),

    /** Subjects of a class that pass the Assigned / Published filters. */
    subjectsForClass(item) {
      const sorted = this.getSortedSubjects(item && item.subjectsHomework);
      if (!this.hasActiveFilter) return sorted;
      return sorted.filter((detail) => {
        const assigned = this.isDone(detail.homeworkStatus);
        const published = this.isDone(detail.publishedStatus);
        if (this.filterAssigned !== "all") {
          const want = this.filterAssigned === "yes";
          if (assigned !== want) return false;
        }
        if (this.filterPublished !== "all") {
          const want = this.filterPublished === "yes";
          if (published !== want) return false;
        }
        return true;
      });
    },

    setAssignedFilter(value) {
      this.filterAssigned = this.filterAssigned === value ? "all" : value;
    },

    setPublishedFilter(value) {
      this.filterPublished = this.filterPublished === value ? "all" : value;
    },

    clearFilters() {
      this.filterAssigned = "all";
      this.filterPublished = "all";
    },

    /** Convert a summary figure into a share of the total. */
    statShare(value) {
      const total = Number(this.summary.totalHomework) || 0;
      if (total <= 0) return 0;
      return Math.min(100, Math.round((Number(value) || 0) / total) * 100);
    },

    /** The API encodes status as "✅ Assigned" / "❌ Not Assigned". */
    isDone(status) {
      return String(status || "").includes("✅");
    },

    /** Strip the emoji prefix and fall back to a readable label. */
    statusLabel(status, pendingLabel, doneLabel) {
      if (!status) return pendingLabel;
      const cleaned = String(status)
        .replace(/[✅❌]\s*/g, "")
        .trim();
      if (!cleaned) return this.isDone(status) ? doneLabel : pendingLabel;
      return cleaned;
    },

    /** How many subjects this class/section has. */
    classSubjectCount(item) {
      const list = item && item.subjectsHomework;
      return Array.isArray(list) ? list.length : 0;
    },

    getVariant(status) {
      return status ? "light-success" : "light-danger";
    },

    getStatusClass(status) {
      return this.isDone(status) ? "status-success" : "status-danger";
    },

    getSortedSubjects(subjects) {
      if (!subjects || !Array.isArray(subjects)) return [];

      // Create a copy to avoid mutating the original array
      return [...subjects].sort((a, b) => {
        // First sort by published status (published first)
        const aPublished = this.isDone(a.publishedStatus);
        const bPublished = this.isDone(b.publishedStatus);

        if (aPublished && !bPublished) return -1;
        if (!aPublished && bPublished) return 1;

        // If both have same published status, sort by homework status (assigned first)
        const aAssigned = this.isDone(a.homeworkStatus);
        const bAssigned = this.isDone(b.homeworkStatus);

        if (aAssigned && !bAssigned) return -1;
        if (!aAssigned && bAssigned) return 1;

        // If both have same status, sort alphabetically by subject name
        return String(a.subject || "").localeCompare(String(b.subject || ""));
      });
    },

    async LoadData() {
      if (!this.hwDate) return;
      this.loading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "homework/LoadDashboard?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.campusID +
            "&type=homework&dt=" +
            this.hwDate,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.items = Array.isArray(response) ? response : [];

        // Calculate summary statistics
        this.calculateSummary();
      } finally {
        this.loading = false;
      }
    },

    calculateSummary() {
      let totalHomework = 0;
      let totalAssigned = 0;
      let totalPublished = 0;
      let totalUnAssigned = 0;
      let totalUnPublished = 0;
      let markedClasses = 0;

      this.items.forEach((item) => {
        if (item.subjectsHomework && Array.isArray(item.subjectsHomework)) {
          item.subjectsHomework.forEach((subject) => {
            totalHomework++;

            // Check homework status
            if (
              subject.homeworkStatus &&
              subject.homeworkStatus.includes("✅")
            ) {
              totalAssigned++;
            } else {
              totalUnAssigned++;
            }
            // Check published status
            if (
              subject.publishedStatus &&
              subject.publishedStatus.includes("✅")
            ) {
              totalPublished++;
            } else {
              totalUnPublished++;
            }
          });

          // Check if this class has any homework assigned
          if (
            item.subjectsHomework.some(
              (subject) =>
                subject.homeworkStatus && subject.homeworkStatus.includes("✅")
            )
          ) {
            markedClasses++;
          }
        }
      });

      this.summary = {
        totalHomework,
        totalAssigned,
        totalPublished,
        totalUnAssigned,
        totalUnPublished,
        totalCampuses: this.items.length,
        markedCampuses: markedClasses,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* ── Header ───────────────────────────────────────────── */
.hw-card__icon {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #ea580c;
}

.hw-card__header {
  padding-bottom: 0.85rem !important;
}

.hw-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
}

.hw-card__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.hw-card__date {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.4rem 0.85rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  color: #64748b;
  cursor: pointer;
}

.hw-card__date-input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-width: 6.5rem;
  font-weight: 700;
  font-size: 0.85rem;
  color: #1e293b !important;
  cursor: pointer;
}

.hw-card__refresh {
  display: inline-flex;
  align-items: center;
  height: 38px;
  border-radius: 12px !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding-inline: 1rem !important;
  white-space: nowrap;
}

/* ── Class completion ─────────────────────────────────── */
.hw-progress-wrap {
  padding: 0.85rem 1.15rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
}

.hw-progress__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}

.hw-progress__title {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.hw-progress__value {
  font-size: 0.82rem;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;

  strong {
    color: #0f172a;
    font-weight: 800;
  }
}

.hw-progress {
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.hw-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #fdba74 0%, #fb923c 55%, #ea580c 100%);
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

/* ── Stat tiles ───────────────────────────────────────── */
.hw-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.6rem;
  padding: 0.9rem 1.15rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

@media (max-width: 1199px) {
  .hw-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .hw-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.hw-stat {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  border: 1px solid #eef2f7;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  min-width: 0;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.07);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    border-radius: 3px;
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 7px;
    flex-shrink: 0;
  }

  &__label {
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.03em;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.1rem;
  }

  &__meter {
    position: relative;
    flex: 1;
    height: 4px;
    border-radius: 999px;
    background: #eef2f7;
    overflow: hidden;
    min-width: 0;
  }

  &__meter-fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    transition: width 0.45s cubic-bezier(0.22, 1, 0.36, 1);

    &--ok {
      background: linear-gradient(90deg, #34d399, #059669);
    }
    &--pub {
      background: linear-gradient(90deg, #7dd3fc, #0284c7);
    }
    &--pending {
      background: linear-gradient(90deg, #fdba74, #ea580c);
    }
    &--muted {
      background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    }
  }

  &__pct {
    font-size: 0.62rem;
    font-weight: 800;
    color: #64748b;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__hint {
    font-size: 0.62rem;
    font-weight: 600;
    color: #b6c0cf;
    white-space: nowrap;
  }

  &--total::before {
    background: linear-gradient(180deg, #a5b4fc, #4f46e5);
  }
  &--ok::before {
    background: linear-gradient(180deg, #34d399, #059669);
  }
  &--published::before {
    background: linear-gradient(180deg, #38bdf8, #0284c7);
  }
  &--pending::before {
    background: linear-gradient(180deg, #fdba74, #ea580c);
  }
  &--muted::before {
    background: linear-gradient(180deg, #cbd5e1, #94a3b8);
  }

  &--total .hw-stat__icon {
    background: #eef2ff;
    color: #4f46e5;
  }
  &--ok .hw-stat__icon {
    background: #ecfdf5;
    color: #059669;
  }
  &--published .hw-stat__icon {
    background: #e0f2fe;
    color: #0284c7;
  }
  &--pending .hw-stat__icon {
    background: #fff7ed;
    color: #ea580c;
  }
  &--muted .hw-stat__icon {
    background: #f1f5f9;
    color: #64748b;
  }
}

/* ── Search + filters ─────────────────────────────────── */
.hw-toolbar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin: 0.85rem 1.15rem;
}

.hw-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 14rem;
  min-width: 0;
  padding: 0.45rem 0.7rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #fed7aa;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.14);
  }

  &__icon {
    display: inline-flex;
    color: #94a3b8;
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 0.84rem;
    font-weight: 600;
    color: #1e293b;

    &::placeholder {
      color: #a8b3c2;
      font-weight: 500;
    }
  }

  &__count {
    font-size: 0.72rem;
    font-weight: 700;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

.hw-filters {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;

  &__label {
    font-size: 0.64rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;

    &:not(:first-child) {
      margin-left: 0.35rem;
    }
  }

  &__group {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.15rem;
    border-radius: 10px;
    background: #f1f5f9;
    border: 1px solid #e8edf4;
  }

  &__clear {
    display: inline-flex;
    align-items: center;
    height: 1.85rem;
    padding: 0 0.6rem;
    border: 1px dashed #e2e8f0;
    border-radius: 9px;
    background: #fff;
    color: #64748b;
    font-size: 0.68rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
      color: #334155;
      background: #f8fafc;
    }
  }
}

.hw-filter {
  border: 0;
  background: transparent;
  color: #7b8797;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  padding: 0.34rem 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    color: #334155;
  }

  &.is-active {
    background: #fff;
    color: #0f172a;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
  }

  &--ok.is-active {
    background: #dcfce7;
    color: #15803d;
    box-shadow: inset 0 0 0 1px #bbf7d0;
  }

  &--bad.is-active {
    background: #fee2e2;
    color: #b91c1c;
    box-shadow: inset 0 0 0 1px #fecaca;
  }
}

.hw-filter-summary {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  margin: -0.45rem 1.15rem 0.75rem;
  padding: 0.3rem 0.65rem;
  width: fit-content;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
  font-size: 0.7rem;
  font-weight: 700;

  &__sep {
    opacity: 0.5;
    margin: 0 0.2rem;
  }
}

.hw-subjects-empty {
  padding: 0.5rem 0;
  font-size: 0.74rem;
  font-weight: 600;
  color: #b6c0cf;
  font-style: italic;
}

/* ── Table ────────────────────────────────────────────── */
.hw-table-wrap {
  border-top: 1px solid #eef2f7;
}

.hw-table {
  font-size: 0.84rem;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    border-top: none !important;
    padding: 0.6rem 0.85rem !important;
    vertical-align: middle;
    box-shadow: 0 1px 0 #e2e8f0;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
  }

  tbody td {
    padding: 0.75rem 0.85rem !important;
    vertical-align: top;
    border-color: #f1f5f9 !important;
  }

  tbody tr:nth-child(even) {
    background: #fafbfc;
  }

  tbody tr td:first-child {
    box-shadow: inset 3px 0 0 transparent;
    transition: box-shadow 0.15s ease;
  }

  tbody tr:hover td:first-child {
    box-shadow: inset 3px 0 0 #fb923c;
  }
}

/* Class & section cell */
.hw-class {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 8rem;

  &__name {
    font-weight: 800;
    font-size: 0.84rem;
    color: #1e293b;
    line-height: 1.3;
    word-break: break-word;
  }

  &__meta {
    font-size: 0.66rem;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
  }
}

/* Subject cards */
.hw-subjects {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 0.55rem;
}

.hw-subject {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.6rem 0.7rem;
  border-radius: 12px;
  border: 1px solid #eef2f7;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;

  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.07);
    transform: translateY(-1px);
  }

  &.is-done {
    border-color: #d1fae5;
    background: linear-gradient(180deg, #f7fefb 0%, #fff 60%);
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.4rem;
    min-width: 0;
  }

  &__name {
    font-size: 0.8rem;
    font-weight: 800;
    color: #1e293b;
    line-height: 1.3;
    word-break: break-word;
  }

  &__teacher {
    flex-shrink: 0;
    max-width: 45%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.62rem;
    font-weight: 700;
    color: #7c3aed;
    background: #f5f3ff;
    border: 1px solid #ede9fe;
    border-radius: 999px;
    padding: 0.1rem 0.45rem;
    line-height: 1.5;
  }

  &__status {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}

/* Status chips */
.hw-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.24rem;
  padding: 0.16rem 0.45rem;
  border-radius: 999px;
  font-size: 0.64rem;
  font-weight: 800;
  line-height: 1.35;
  white-space: nowrap;

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &--ok {
    background: #dcfce7;
    color: #15803d;
  }

  &--pub {
    background: #e0f2fe;
    color: #0369a1;
  }

  &--bad {
    background: #fef2f2;
    color: #b91c1c;
  }
}

/* ── Skeleton ─────────────────────────────────────────── */
.hw-skeleton {
  padding: 0.5rem 0.85rem 0.75rem;
}

.hw-skeleton__group {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 1rem;
  padding: 0.7rem 0;
  border-bottom: 1px solid #f5f7fa;

  &:last-child {
    border-bottom: none;
  }
}

.hw-skeleton__class,
.hw-skeleton__card {
  display: block;
  border-radius: 10px;
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 40%, #eef2f7 80%);
  background-size: 200% 100%;
  animation: hwSk 1.25s ease-in-out infinite;
}

.hw-skeleton__class {
  height: 0.95rem;
  width: 78%;
}

.hw-skeleton__cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
}

.hw-skeleton__card {
  height: 62px;
}

@keyframes hwSk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.hw-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
}

@media (max-width: 767px) {
  .hw-subjects {
    grid-template-columns: minmax(0, 1fr);
  }

  .hw-skeleton__group {
    grid-template-columns: minmax(0, 1fr);
  }

  .hw-skeleton__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
